import { match } from 'ts-pattern';

import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

export type PromoCardsVisibility = {
  showSplitEntry: boolean;
  showMarketplace: boolean;
};

type PromoCardTreatment = {
  treatment: string;
  config?: unknown;
} | null;

type ContestVisibilityInput = Pick<
  FullContest,
  'id' | 'split_entry_enabled' | 'marketplace_enabled'
>;

const HIDDEN: PromoCardsVisibility = { showSplitEntry: false, showMarketplace: false };

/**
 * Resolves the visibility of the Split Entry and Marketplace promo cards from the
 * `ENABLE_CONTEST_DETAIL_MARKETPLACE_PROMO` treatment.
 *
 * - `off` / not-ready / unknown (e.g. `control`) → both hidden.
 * - `on` → each card follows its contest API flag (`split_entry_enabled` / `marketplace_enabled`).
 * - `manual` → the treatment config is `{ contestIds: [...] }` (Mixpanel Dynamic Config values
 *   must be objects); if the contest is in the allow-list BOTH cards show regardless of the API
 *   flags, otherwise both hidden.
 */
export function resolvePromoCardVisibility(
  isReady: boolean,
  treatment: PromoCardTreatment,
  contestData: ContestVisibilityInput
): PromoCardsVisibility {
  if (!isReady || !treatment) {
    return HIDDEN;
  }

  return match(treatment.treatment)
    .with('on', () => ({
      showSplitEntry: !!contestData.split_entry_enabled,
      showMarketplace: !!contestData.marketplace_enabled,
    }))
    .with('manual', () => {
      const configured = (treatment.config as { contestIds?: unknown } | undefined)?.contestIds;
      const contestIds = Array.isArray(configured)
        ? configured.filter((id): id is string => typeof id === 'string')
        : [];
      const isAllowlisted = contestIds.includes(contestData.id);
      return { showSplitEntry: isAllowlisted, showMarketplace: isAllowlisted };
    })
    .otherwise(() => HIDDEN);
}
